import { useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { App, Button, DatePicker, Form, Input, InputNumber } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { expenseDataSchema, type ExpenseData, type ExpenseRow } from '@super-mega/server/contracts/expense'
import { Authorized } from '@/features/authorization/authorized.tsx'
import { FinanceSelect, PayeeQuickCreate } from '@/features/bill/index.ts'
import { ExpenseBusinessEditor } from '@/features/master-data/index.ts'
import { useTRPC } from '@/trpc/react.ts'

type Values = Omit<ExpenseData, 'dueAt'> & { dueAt: Dayjs }
export function ExpenseEditor({
  editing,
  canCreateBusiness = false,
  onClose,
  onSaved
}: {
  editing: ExpenseRow | 'new'
  canCreateBusiness?: boolean
  onClose: () => void
  onSaved: () => Promise<unknown>
}) {
  const trpc = useTRPC(),
    { message } = App.useApp()
  const cache = useQueryClient()
  const [open, setOpen] = useState(true)
  const [creatingBusiness, setCreatingBusiness] = useState(false)
  const [form] = Form.useForm<Values>()
  const [writeKey] = useState(() => crypto.randomUUID())
  const save = useMutation(trpc.expense.save.mutationOptions())
  return (
    <>
      <ModalForm<Values>
        name="expense-editor"
        open={open}
        onOpenChange={setOpen}
        title={editing === 'new' ? '新建支出账单' : '编辑支出账单'}
        form={form}
        width="42rem"
        layout="vertical"
        initialValues={editing === 'new' ? { dueAt: dayjs() } : { ...editing, dueAt: dayjs(editing.dueAt) }}
        modalProps={{
          destroyOnHidden: true,
          mask: { closable: false },
          afterClose: onClose,
          onCancel: () => setOpen(false)
        }}
        submitter={{
          searchConfig: { submitText: '保存' },
          resetButtonProps: { onClick: () => setOpen(false) },
          submitButtonProps: { loading: save.isPending }
        }}
        onFinish={async (values) => {
          try {
            await save.mutateAsync({
              id: editing === 'new' ? undefined : editing.id,
              version: editing === 'new' ? undefined : editing.version,
              writeKey,
              data: expenseDataSchema.parse({ ...values, dueAt: values.dueAt.format('YYYY-MM-DD') })
            })
            await onSaved()
            setOpen(false)
            void message.success('支出记录已保存')
            return true
          } catch (error) {
            void message.error(error instanceof Error ? error.message : '保存失败')
            return false
          }
        }}
      >
        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
          <Form.Item name="organizationId" label="付款主体" rules={[{ required: true, message: '请选择付款主体' }]}>
            <FinanceSelect purpose="expense.read" kind="organization" />
          </Form.Item>
          <Form.Item name="payeeId" label="付款对象" rules={[{ required: true, message: '请选择付款对象' }]}>
            <PayeeQuickCreate />
          </Form.Item>
          <Form.Item label="业务" required htmlFor="expense-editor_expenseBusinessId">
            <div className="grid grid-cols-[1fr_auto] gap-2">
              <Form.Item name="expenseBusinessId" noStyle rules={[{ required: true, message: '请选择业务' }]}>
                <FinanceSelect purpose="expense.read" kind="expenseBusiness" />
              </Form.Item>
              {canCreateBusiness && (
                <Authorized permissions={['expense.create']}>
                  <Button onClick={() => setCreatingBusiness(true)}>新增业务</Button>
                </Authorized>
              )}
            </div>
          </Form.Item>
          <Form.Item name="dueAt" label="应付日期" rules={[{ required: true, message: '请选择应付日期' }]}>
            <DatePicker className="w-full" />
          </Form.Item>
        </div>
        <Form.Item
          name="amount"
          label="金额（元）"
          rules={[
            { required: true, message: '请输入金额' },
            {
              validator: (_, value) =>
                expenseDataSchema.shape.amount.safeParse(value).success
                  ? Promise.resolve()
                  : Promise.reject(new Error('请输入非负金额，最多两位小数'))
            }
          ]}
        >
          <InputNumber<string> stringMode min="0" precision={2} className="w-full!" placeholder="请输入金额" />
        </Form.Item>
        <Form.Item name="note" label="备注">
          <Input.TextArea rows={3} maxLength={5000} showCount />
        </Form.Item>
      </ModalForm>
      {creatingBusiness && (
        <ExpenseBusinessEditor
          editing="new"
          onClose={() => setCreatingBusiness(false)}
          onSaved={async (saved) => {
            await Promise.all([
              cache.invalidateQueries(trpc.bill.directoryOptions.queryFilter({ kind: 'expenseBusiness' })),
              cache.invalidateQueries(trpc.expenseBusiness.list.queryFilter()),
              cache.invalidateQueries(trpc.expenseBusiness.options.queryFilter())
            ])
            form.setFieldValue('expenseBusinessId', saved.id)
          }}
        />
      )}
    </>
  )
}
